import { normalizeName } from "../parser/item-input.js";
import type { CategoryKey } from "../categories/generated.js";
import type { Product, Unit } from "../types/entities.js";

/** Was der Katalog über einen eingetippten Namen weiß. */
export interface CatalogMatch {
  product: Product | null;
  category: CategoryKey | null;
  unit: Unit | null;
}

/**
 * Findet das Produkt zu einem eingetippten Namen. Erst exakt über den
 * normalisierten Namen, dann über den Anfang, zuletzt über eine Teilfolge.
 * Bei mehreren Treffern gewinnt das häufiger gekaufte.
 */
export function matchProduct(raw: string, products: readonly Product[]): CatalogMatch {
  const needle = normalizeName(raw);
  if (needle.length === 0) return { product: null, category: null, unit: null };

  const alive = products.filter((product) => product.deleted_at === null);
  const byUse = (a: Product, b: Product) => b.use_count - a.use_count || a.name.localeCompare(b.name, "de-DE");

  const exact = alive.filter((product) => product.normalized_name === needle).sort(byUse);
  const prefix = alive.filter((product) => product.normalized_name.startsWith(needle)).sort(byUse);
  const inside = alive.filter((product) => product.normalized_name.includes(needle)).sort(byUse);

  const product = exact[0] ?? prefix[0] ?? inside[0] ?? null;
  return { product, category: product?.category ?? null, unit: product?.default_unit ?? null };
}

/**
 * Vorschläge für die Autovervollständigung. Reihenfolge: Favoriten zuerst,
 * dann nach Häufigkeit. Wer zweimal am Tag Milch kauft, soll sie oben sehen.
 */
export function suggestProducts(raw: string, products: readonly Product[], limit = 8): Product[] {
  const needle = normalizeName(raw);
  const alive = products.filter((product) => product.deleted_at === null);

  const pool =
    needle.length === 0
      ? alive
      : alive.filter((product) => product.normalized_name.includes(needle));

  return [...pool]
    .sort((a, b) => {
      if (a.is_favorite !== b.is_favorite) return a.is_favorite ? -1 : 1;
      const starts = Number(b.normalized_name.startsWith(needle)) - Number(a.normalized_name.startsWith(needle));
      if (starts !== 0) return starts;
      if (a.use_count !== b.use_count) return b.use_count - a.use_count;
      return a.name.localeCompare(b.name, "de-DE");
    })
    .slice(0, limit);
}
